import { useState, useEffect, type ReactNode } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { ExternalLink } from 'lucide-react';
import { PageHeader } from '@/components/layout/PageHeader';
import { ListPageLayout } from '@/components/layout/ListPageLayout';
import { DetailRightPanel } from '@/components/layout/DetailRightPanel';
import { DetailProfile } from '@/components/layout/DetailProfile';
import { Button } from '@/components/ui/Button';
import { AiReportScheduleModal } from '@/components/reports/AiReportScheduleModal';
import { AiReportShareModal } from '@/components/reports/AiReportShareModal';
import { usePatientDetail } from '@/hooks/usePatientDetail';
import { usePageContext } from '@/hooks/usePageContext';
import { aiReportsApi, type AiReportResult } from '@/services/aiReports.service';
import { getErrorMessage } from '@/services/apiClient';

/**
 * Renders one AI-generated ad-hoc report (from the Assistant's run_report tool).
 * Polls while the async job runs, then shows a generic dynamic table of the
 * result columns + a bounded preview, with CSV/Excel download of the full set.
 *
 * Patient name / ID cells are clickable and open the shared patient detail
 * right panel (same pattern as TasksPage / PatientsPage). The visible id is the
 * patient-facing displayID; the internal patientID rides along in a hidden
 * `_patientId` column used only to open the panel.
 *
 * Deliberately NOT the legacy ReportResultViewer, which only renders the fixed
 * numeric chart-matrix (series × buckets) and can't display arbitrary columns.
 */
export default function AiReportResultViewer() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const reportId = Number(id);

  const [selectedPatient, setSelectedPatient] = useState<{ patientId: number; patientName: string } | null>(null);
  const [panelExpandTrigger, setPanelExpandTrigger] = useState(0);
  const [scheduleOpen, setScheduleOpen] = useState(false);
  const [shareOpen, setShareOpen] = useState(false);
  const { detail, loading } = usePatientDetail(selectedPatient ? String(selectedPatient.patientId) : null);

  const selectPatient = (patientId: number, patientName: string) => {
    if (!patientId) return;
    setSelectedPatient({ patientId, patientName });
    setPanelExpandTrigger((t) => t + 1); // forces DetailRightPanel open
  };

  const query = useQuery({
    queryKey: ['ai-report', reportId],
    queryFn: () => aiReportsApi.get(reportId),
    enabled: Number.isFinite(reportId) && reportId > 0,
    // Poll while the job is still running; stop once terminal.
    refetchInterval: (q) => (q.state.data?.status === 'pending' ? 2500 : false),
  });

  const report = query.data;

  // Register this page with the AI sidebar so it can chat/generate reports here
  // (without a page context the sidebar short-circuits with "not enough context").
  const updateAIContext = usePageContext('AI Report');
  useEffect(() => {
    if (!report) return;
    const rows = report.rowCount ?? report.preview.length;
    updateAIContext({ dataSummary: `Viewing AI report "${report.prompt}" — ${rows} rows` });
  }, [report, updateAIContext]);

  return (
    <ListPageLayout
      filterSidebar={null}
      rightPanel={
        selectedPatient ? (
          <DetailRightPanel expandTrigger={panelExpandTrigger}>
            <div className="flex h-full min-h-0 w-full flex-col bg-white">
              <div className="flex items-center justify-between gap-2 border-b border-slate-200 px-3 py-1.5">
                <span className="truncate text-sm font-medium text-slate-700">
                  {selectedPatient.patientName}
                </span>
                <button
                  type="button"
                  onClick={() => navigate(`/patients/${selectedPatient.patientId}`)}
                  className="inline-flex items-center gap-1 rounded-md px-2 py-1 text-xs font-medium text-blue-600 hover:bg-blue-50"
                >
                  <ExternalLink className="h-3.5 w-3.5" /> Open
                </button>
              </div>
              <div className="min-h-0 flex-1">
                <DetailProfile
                  detail={detail}
                  loading={loading}
                  onSectionNavigate={(section) =>
                    navigate(`/patients/${selectedPatient.patientId}?section=${section}`)
                  }
                />
              </div>
            </div>
          </DetailRightPanel>
        ) : undefined
      }
    >
      <div className="flex-1 overflow-y-auto p-4">
        <PageHeader
          title="AI Report"
          subtitle={report?.prompt ? report.prompt : 'Generated from the AI Assistant'}
          actions={
            report?.canManage ? (
              report.scheduleId ? (
                <Button
                  variant="secondary"
                  onClick={() => navigate(`/reports/ai-schedules/${report.scheduleId}`)}
                >
                  Manage schedule
                </Button>
              ) : (
                <div className="flex gap-2">
                  <Button variant="secondary" onClick={() => setScheduleOpen(true)}>Make recurring</Button>
                  <Button variant="secondary" onClick={() => setShareOpen(true)}>Share</Button>
                </div>
              )
            ) : undefined
          }
        />

        {query.isError && (
          <Banner tone="error">{getErrorMessage(query.error, 'Failed to load this report.')}</Banner>
        )}

        {report && <Body report={report} onSelectPatient={selectPatient} />}

        {!report && query.isLoading && <Banner tone="info">Loading…</Banner>}

        {report && report.canManage && !report.scheduleId && (
          <>
            <AiReportScheduleModal
              isOpen={scheduleOpen}
              onClose={() => setScheduleOpen(false)}
              sourceRunId={reportId}
              defaultTitle={report.prompt}
              onSaved={(sid) => navigate(`/reports/ai-schedules/${sid}`)}
            />
            <AiReportShareModal
              isOpen={shareOpen}
              onClose={() => setShareOpen(false)}
              targetType="run"
              targetId={reportId}
              targetTitle={report.prompt}
            />
          </>
        )}
      </div>
    </ListPageLayout>
  );
}

function Body({
  report,
  onSelectPatient,
}: {
  report: AiReportResult;
  onSelectPatient: (patientId: number, patientName: string) => void;
}) {
  if (report.status === 'pending') {
    return (
      <div className="flex flex-col items-center justify-center gap-3 py-20 text-slate-600">
        <div className="h-9 w-9 animate-spin rounded-full border-[3px] border-slate-200 border-t-blue-500" />
        <p className="text-sm">Running your report… this can take a few seconds (longer for large reports).</p>
      </div>
    );
  }

  if (report.status === 'failed') {
    return (
      <Banner tone="error">
        <p className="font-medium">This report couldn’t be generated.</p>
        <p className="mt-1 whitespace-pre-wrap text-sm">{report.error || 'Unknown error.'}</p>
        <p className="mt-2 text-sm text-slate-600">
          Ask the Assistant to adjust the request (e.g. narrow the date range or the columns) and try again.
        </p>
      </Banner>
    );
  }

  // ready
  const rowCount = report.rowCount ?? report.preview.length;
  const showingPreview = report.previewCount < rowCount;
  // Defensive: never render `_`-prefixed helper columns (e.g. `_patientId`).
  const columns = report.columns.filter((c) => !c.startsWith('_'));
  const nameCol = columns.find(isPatientNameCol);

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="text-sm text-slate-600">
          <span className="font-semibold text-slate-900">{rowCount.toLocaleString()}</span> row
          {rowCount === 1 ? '' : 's'}
          {report.truncated && ' (capped at the row ceiling)'}
          {showingPreview && (
            <span className="ml-1 text-slate-500">
              · showing the first {report.previewCount.toLocaleString()} below — download for the full set
            </span>
          )}
        </div>
        <div className="flex gap-2">
          {report.xlsxUrl && (
            <a
              href={report.xlsxUrl}
              className="rounded-lg bg-blue-600 px-3.5 py-2 text-sm font-medium text-white hover:bg-blue-700"
            >
              Download Excel
            </a>
          )}
          {report.csvUrl && (
            <a
              href={report.csvUrl}
              className="rounded-lg border border-slate-300 px-3.5 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50"
            >
              Download CSV
            </a>
          )}
        </div>
      </div>

      {rowCount === 0 || columns.length === 0 ? (
        <Banner tone="info">This report returned no rows.</Banner>
      ) : (
        <div className="overflow-x-auto rounded-lg border border-slate-200">
          <table className="min-w-full border-collapse text-sm">
            <thead className="bg-slate-50">
              <tr>
                {columns.map((col) => (
                  <th
                    key={col}
                    className="whitespace-nowrap border-b border-slate-200 px-3 py-2 text-left font-semibold text-slate-700"
                  >
                    {col}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {report.preview.map((row, i) => {
                const pid = patientIdOf(row);
                const patientName = nameCol ? formatCell(row[nameCol]) : '';
                return (
                  <tr key={i} className="odd:bg-white even:bg-slate-50/50">
                    {columns.map((col) => {
                      const text = formatCell(row[col]);
                      const clickable = pid !== null && (isPatientNameCol(col) || isPatientIdCol(col));
                      return (
                        <td
                          key={col}
                          className="whitespace-nowrap border-b border-slate-100 px-3 py-1.5 text-slate-800"
                        >
                          {clickable ? (
                            <button
                              type="button"
                              className="text-left text-blue-600 hover:text-blue-800 hover:underline"
                              onClick={() => onSelectPatient(pid, patientName || text)}
                            >
                              {text}
                            </button>
                          ) : (
                            text
                          )}
                        </td>
                      );
                    })}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ─── Patient-column detection ─────────────────────────────────────────────────

function normalizeHeader(h: string): string {
  return h.toLowerCase().replace(/[\s_]+/g, '');
}

function isPatientNameCol(h: string): boolean {
  return normalizeHeader(h) === 'patientname';
}

function isPatientIdCol(h: string): boolean {
  const n = normalizeHeader(h);
  return n === 'patientid' || n === 'displayid';
}

/** The internal patientID for a row, from the hidden `_patientId` helper column. */
function patientIdOf(row: Record<string, unknown>): number | null {
  const v = row['_patientId'] ?? row['_patientID'] ?? row['_patient_id'];
  const n = typeof v === 'number' ? v : Number(v);
  return Number.isFinite(n) && n > 0 ? n : null;
}

function formatCell(value: unknown): string {
  if (value === null || value === undefined) return '';
  if (typeof value === 'object') return JSON.stringify(value);
  return String(value);
}

function Banner({ tone, children }: { tone: 'error' | 'info'; children: ReactNode }) {
  const cls =
    tone === 'error'
      ? 'border-red-200 bg-red-50 text-red-800'
      : 'border-slate-200 bg-slate-50 text-slate-700';
  return <div className={`rounded-lg border p-4 text-sm ${cls}`}>{children}</div>;
}
